import { useEffect, useMemo, useState } from 'react';

export const monthNames = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь',
];

export const weekdays = ['ПН', 'ВТ', 'СР', 'ЧТ', 'ПТ', 'СБ', 'ВС'];

export const getDaysInMonth = (year: number, month: number) =>
  new Date(year, month + 1, 0).getDate();

export const getMondayIndex = (date: Date) => (date.getDay() + 6) % 7;

export const isSameDay = (a?: Date, b?: Date) =>
  !!a &&
  !!b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

export const startOfDay = (d: Date) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate());

export type CalendarCell = { date: Date; outside: boolean };

const buildMonthGrid = (year: number, month: number): CalendarCell[] => {
  const firstOfMonth = new Date(year, month, 1);
  const startIndex = getMondayIndex(firstOfMonth);
  const daysInCurrent = getDaysInMonth(year, month);
  const daysInPrev = getDaysInMonth(year, month - 1);

  const cells = Array.from({ length: 42 }).map((_, i) => {
    let date: Date;
    let outside = false;

    if (i < startIndex) {
      const day = daysInPrev - startIndex + i + 1;
      date = new Date(year, month - 1, day);
      outside = true;
    } else if (i < startIndex + daysInCurrent) {
      const day = i - startIndex + 1;
      date = new Date(year, month, day);
    } else {
      const day = i - (startIndex + daysInCurrent) + 1;
      date = new Date(year, month + 1, day);
      outside = true;
    }

    return { date, outside };
  });

  return cells;
};

interface UseCalendarParams {
  value?: Date;
  onChange?: (date: Date) => void;
  minDate?: Date;
  maxDate?: Date;
}

export function useCalendar({
  value,
  onChange,
  minDate,
  maxDate,
}: UseCalendarParams) {
  const today = new Date();
  const initial = value ?? today;

  const [currentYear, setCurrentYear] = useState<number>(initial.getFullYear());
  const [currentMonth, setCurrentMonth] = useState<number>(initial.getMonth());
  const [internalValue, setInternalValue] = useState<Date | undefined>(value);

  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  const selectedDate = internalValue ?? value;

  const grid = useMemo(
    () => buildMonthGrid(currentYear, currentMonth),
    [currentMonth, currentYear]
  );

  const isDateDisabled = (date: Date, outside: boolean) => {
    if (outside) return true;
    if (minDate && startOfDay(date) < startOfDay(minDate)) return true;
    if (maxDate && startOfDay(date) > startOfDay(maxDate)) return true;
    return false;
  };

  const goPrevMonth = () => {
    setCurrentMonth(prev => {
      if (prev === 0) {
        setCurrentYear(y => y - 1);
        return 11;
      }
      return prev - 1;
    });
  };

  const goNextMonth = () => {
    setCurrentMonth(prev => {
      if (prev === 11) {
        setCurrentYear(y => y + 1);
        return 0;
      }
      return prev + 1;
    });
  };

  const goPrevYear = () => setCurrentYear(y => y - 1);
  const goNextYear = () => setCurrentYear(y => y + 1);

  const selectDate = (date: Date, disabled: boolean) => {
    if (disabled) return;
    setInternalValue(date);
    onChange?.(date);
  };

  return {
    currentYear,
    currentMonth,
    grid,
    selectedDate,
    isDateDisabled,
    goPrevMonth,
    goNextMonth,
    goPrevYear,
    goNextYear,
    selectDate,
  };
}
